import { useContext, useMemo, useState } from "react";
import currency from "currency.js";
import { useTranslation } from "react-i18next";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { SettingsContext } from "../../../contexts/SettingsContext";
import {
  PercentSlider,
  PriceChips,
  QuickActions,
  SlippageControl,
} from "../../shared/NumericField";
import { bookRefPrices, orderPriceDirection } from "../../../utils/amount/price";
import { worstFillPrice } from "../../../utils/amount/slippage";
import { SIZE_PRESETS } from "./usePerpSizePercent";
import { FormValidatorKeysEnum } from "../../../enums/form";
import { AssetResponse } from "../../../utils/asset";
import {
  alignPriceToStep,
  floorToStepString,
  roundToStepSize,
} from "../../../utils/format";
import { USDC_TERMS_PRECISION, USDC_TERMS_STEP } from "./PerpsTradeForm.helpers";
import { InputsWrapper, GapWrapper } from "../style";
import {
  OrderSizeInput,
  PerpFormFieldKeyEnum,
  PriceInput,
  TriggerPriceInput,
} from "./form";
import { PerpsTradeFormCtx } from "./PerpsTradeForm.ctx";
import { amountIsNumeric } from "./amountValidators";

export default function PerpsTradeFormOrderInputs({
  ctx,
}: {
  ctx: PerpsTradeFormCtx;
}) {
  const {
    stopType,
    markPrice,
    contractPriceStep,
    register,
    isLoading,
    triggerPriceValidateFn,
    setValue,
    trigger,
    errors,
    orderType,
    limitPriceValidateFn,
    customError,
    tradeUseUSDCTerms,
    currentPosition,
    perpInstrument,
    calculateTotalValueWithSize,
    amount,
    getRealContractSize,
    setTradeUseUSDCTerms,
    verifyNotEnoughBalance,
    reduceOnly,
    showAmountValidation,
    insufficientLiquidity,
    formTranslations,
    isPerp,
    isSpot,
    sizePercent,
    setSizePercent,
    activeSizePercent,
    sizeMax,
    sizeSteps,
    orderbookData,
    orderDirection,
    spotTokenBalances,
    balanceUnknownReason,
    price,
  } = ctx;

  // Max slippage bounds every market order (perp and spot) and every market
  // close; it is one standing preference, so it is edited where it applies.
  const { slippageBps, setSlippageBps } = useContext(SettingsContext);
  const { t: slippageT } = useTranslation("app", {
    keyPrefix: "common.inputs.slippage",
  });
  const { t: inputsT } = useTranslation("app", { keyPrefix: "common.inputs" });
  // The neutral note for a size that cannot be checked against a balance yet.
  // The MAX chips already show the same text when MAX is unavailable for the
  // same reason; only say it here when they do not.
  const balanceUnknownNote =
    balanceUnknownReason &&
    !(!sizeMax.ok && sizeMax.reason === balanceUnknownReason)
      ? inputsT(`max_unavailable.${balanceUnknownReason}`)
      : undefined;
  const isMarketOrder = orderType === OrderTypeResponse.Market && !stopType;
  // Set when blur moved the typed limit price onto the tick grid, so the
  // change is said out loud instead of happening silently (B-6). It is shown
  // only while the field still holds THAT price on THAT market: the price can
  // also be written without onChange (order-book click, market switch clear),
  // and the note must never describe a price the order no longer has.
  const [priceRounded, setPriceRounded] = useState<{
    aligned: string;
    instrumentId?: string;
    text: string;
  }>();
  const priceRoundedNote =
    priceRounded &&
    priceRounded.instrumentId === perpInstrument?.instrument_id &&
    String(price ?? "") === priceRounded.aligned
      ? priceRounded.text
      : undefined;

  // A buy never pays more than the price typed, a sell never sells for less.
  const priceDirection =
    orderDirection === SideResponse.Sell ? "up" : "down";
  const alignPrice = (value: string | number) =>
    alignPriceToStep(
      value,
      contractPriceStep.price_step,
      contractPriceStep.price_precision,
      priceDirection
    );
  // Mid / Bid / Ask from the live book (decision 6). The mid is only a mid when
  // both sides quote; otherwise it is the mark, and the chips say so. The old
  // single "Mid" chip wrote the MARK under that name.
  const priceRefs = useMemo(
    () =>
      bookRefPrices(
        orderbookData ? { bids: orderbookData.bids, asks: orderbookData.asks } : undefined,
        markPrice === undefined || markPrice === null ? null : String(markPrice),
        sizeSteps.priceStep,
        orderPriceDirection(orderDirection === SideResponse.Sell ? "sell" : "buy")
      ),
    [markPrice, orderDirection, orderbookData, sizeSteps.priceStep]
  );

  // What the slippage bound means in money. A buy is bounded off the ASK it
  // would cross, a sell off the BID; with no touch, the mid (which may be the
  // mark) is the honest reference. An unreadable book gives no figure at all,
  // and the row renders a dash rather than inventing a price.
  const worstFill = useMemo(() => {
    if (!isMarketOrder) return undefined;
    const isBuy = orderDirection !== SideResponse.Sell;
    const reference = (isBuy ? priceRefs.ask : priceRefs.bid) ?? priceRefs.mid;
    const worst = worstFillPrice(
      reference ?? null,
      slippageBps,
      isBuy,
      contractPriceStep.price_precision
    );
    return {
      label: slippageT("worst_fill"),
      // As money, at the market's own precision. A bare "60610.1" sat inches
      // from an order book rendering "$60,010.00" -- same quantity, two
      // different-looking numbers.
      valueText:
        worst === null
          ? undefined
          : currency(worst, { precision: contractPriceStep.price_precision }).format(),
    };
  }, [
    contractPriceStep.price_precision,
    isMarketOrder,
    orderDirection,
    priceRefs,
    slippageBps,
    slippageT,
  ]);

  const markPriceText = markPrice
    ? alignPriceToStep(
        markPrice,
        contractPriceStep.price_step,
        contractPriceStep.price_precision
      )
    : "";

  return (
    <InputsWrapper>
      <GapWrapper>
        {!!stopType && (
          <TriggerPriceInput
            placeholder={markPriceText}
            markPrice={markPriceText || undefined}
            minPrice={contractPriceStep.price_step}
            register={register(PerpFormFieldKeyEnum.TRIGGER_PRICE, {
              disabled: isLoading,
              required: true,
              validate: {
                ...triggerPriceValidateFn,
                [FormValidatorKeysEnum.moreThanZero]: (v) =>
                  parseFloat(v) > 0,
                // The smallest price is one step, not 1/10^precision: the token
                // config's precision can be coarser than the market's step.
                [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                  roundToStepSize(
                    Number(v),
                    contractPriceStep.price_step,
                    contractPriceStep.price_precision
                  ) >= contractPriceStep.price_step,
              },
              onBlur(event) {
                // Floored onto the step as before (not moved by side like a
                // limit price); plain decimals, and an empty field stays empty.
                setValue(
                  PerpFormFieldKeyEnum.TRIGGER_PRICE,
                  alignPriceToStep(
                    event.target.value,
                    contractPriceStep.price_step,
                    contractPriceStep.price_precision,
                    "down"
                  )
                );
              },
            })}
            errors={errors}
          />
        )}
        {orderType === OrderTypeResponse.Limit && (
          <PriceInput
            isStopOrder={!!stopType}
            placeholder={markPriceText}
            minPrice={contractPriceStep.price_step}
            register={register(PerpFormFieldKeyEnum.PRICE, {
              disabled: isLoading,
              required: true,
              validate: {
                ...limitPriceValidateFn,
                [FormValidatorKeysEnum.moreThanZero]: (v) =>
                  parseFloat(v) > 0,
                [FormValidatorKeysEnum.decimalsTooSmall]: (v) =>
                  roundToStepSize(
                    Number(v),
                    contractPriceStep.price_step,
                    contractPriceStep.price_precision
                  ) >= contractPriceStep.price_step,
              },
              onChange() {
                setPriceRounded(undefined);
              },
              onBlur(event) {
                // An empty field stays empty; it used to become "0". Plain
                // decimals only: `.toString()` wrote tiny prices as "4.5e-7".
                const typed = event.target.value;
                const aligned = alignPrice(typed);
                setValue(PerpFormFieldKeyEnum.PRICE, aligned);
                setPriceRounded(
                  typed && aligned && Number(aligned) !== Number(typed)
                    ? {
                        aligned: String(aligned),
                        instrumentId: perpInstrument?.instrument_id,
                        text: inputsT("price_rounded_to_tick", {
                          step: contractPriceStep.price_step,
                          price: aligned,
                          defaultValue: "Rounded to the {{step}} tick: {{price}}",
                        }),
                      }
                    : undefined
                );
              },
            })}
            errors={errors}
            warning={priceRoundedNote}
          />
        )}
        {orderType === OrderTypeResponse.Limit && (
          <PriceChips
            refs={priceRefs}
            disabled={isLoading}
            onPick={(_kind, picked) => {
              // Exactly the string shown on the chip: already on the grid.
              setPriceRounded(undefined);
              setValue(PerpFormFieldKeyEnum.PRICE, picked, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        )}
        <OrderSizeInput
          customError={customError}
          // The message this drives is "Min order size is X", so it must be the floor
          // the chain enforces, not the rounding step. In USDC terms the entry minimum
          // is the 0.01 checked by the validator above -- price_step was never the
          // right number here, it just happened to read as 0.000001.
          minAmountSize={
            tradeUseUSDCTerms ? 0.01 : contractPriceStep.min_order_amount
          }
          existingPositionSize={currentPosition?.amount}
          // Perp sizes against buying power; a spot BUY spends the quote
          // token, a spot SELL sells the base token.
          insufficientKind={
            !isSpot
              ? "buying_power"
              : orderDirection === SideResponse.Sell
              ? "base_balance"
              : "quote_balance"
          }
          insufficientAsset={
            orderDirection === SideResponse.Sell
              ? spotTokenBalances?.base.symbol
              : spotTokenBalances?.quote.symbol
          }
          underlyingAsset={
            perpInstrument?.underlying_asset as AssetResponse
          }
          selectedAsset={
            tradeUseUSDCTerms
              ? undefined
              : perpInstrument?.underlying_asset
          }
          onChangeAsset={(asset) => {
            const stepSize = asset
              ? contractPriceStep.amount_step
              : USDC_TERMS_STEP;
            const precision = asset
              ? contractPriceStep.amount_precision
              : USDC_TERMS_PRECISION;
            let newAmount = 0;

            // change from underlying -> USDC
            // update amount to show TOTAL VALUE
            const noAsset = !asset;
            if (!tradeUseUSDCTerms && noAsset) {
              newAmount = roundToStepSize(
                calculateTotalValueWithSize(amount, false),
                stepSize,
                precision
              );
            } else if (tradeUseUSDCTerms && asset) {
              // change from USDC -> underlying
              // update amount to show CONTRACTS
              newAmount = roundToStepSize(
                getRealContractSize(amount, true),
                stepSize,
                precision
              );
            }

            if (newAmount) {
              setValue(
                PerpFormFieldKeyEnum.AMOUNT,
                floorToStepString(newAmount, stepSize, precision)
              );
            }
            setTradeUseUSDCTerms?.(!asset);
          }}
          register={register(PerpFormFieldKeyEnum.AMOUNT, {
            disabled: isLoading,
            required: true,
            validate: {
              // MUST stay first: react-hook-form's default criteriaMode is
              // "firstError" and it walks this object in insertion order, so
              // whichever check fails first owns the message. `parseFloat("abc")`
              // is NaN and `NaN > 0` is false, so `moreThanZero` used to answer
              // a FORMAT problem with a MAGNITUDE message -- "Min order size is
              // 0.01" for a value that is not a number at all.
              //
              // Number(), not parseFloat(): parseFloat("1abc") is 1, which would
              // let a typo through as a real size.
              [FormValidatorKeysEnum.notANumber]: amountIsNumeric,
              [FormValidatorKeysEnum.moreThanZero]: (v) =>
                parseFloat(v) > 0,
              // The min-size check runs BEFORE the balance check: a size below
              // the market's floor is wrong whatever the balance, and the
              // balance message used to mask "Min order size is X" (B-1).
              [FormValidatorKeysEnum.decimalsTooSmall]: (v) => {
                if (tradeUseUSDCTerms) {
                  // When entering in USDC terms, min is 0.01 USDC (2 decimal places)
                  return Number(v) >= 0.01 || Number(v) === 0;
                }
                // Validate against the ORDER FLOOR, not the rounding step. These used
                // to be the same number on spot, so `amount_step` worked by accident.
                // The backend now reports the token's real precision as the step (BTC
                // 0.00000001) while the chain's floor stays at 0.00001 BTC, so keying
                // off the step here would pass sizes the OrderBook rejects with
                // OrderTooSmall.
                const stepSize = contractPriceStep.amount_step;
                const precision = contractPriceStep.amount_precision;
                const rounded = roundToStepSize(Number(v), stepSize, precision);
                return rounded >= contractPriceStep.min_order_amount;
              },
              [FormValidatorKeysEnum.notEnoughBalance]:
                verifyNotEnoughBalance,
              [FormValidatorKeysEnum.reduceOnlyOrderSizeValid]: (v) => {
                // Cap reduce-only size to the open position for BOTH
                // market and limit, so a reduce-only order can never
                // over-close into a new opposite position.
                if (!reduceOnly) {
                  return true;
                }
                const size = getRealContractSize(v, tradeUseUSDCTerms);
                return currentPosition
                  ? size <= Number(currentPosition.amount)
                  : true;
              },
            },
            onChange() {
              // Slider position is derived from amount via sizePercent
              // memo — no manual sync needed. Leverage is independent.
              trigger(PerpFormFieldKeyEnum.AMOUNT);
            },
            onBlur(event) {
              const { value } = event.target;
              if (value === "") return;
              const stepSize = tradeUseUSDCTerms
                ? USDC_TERMS_STEP
                : contractPriceStep.amount_step;
              const precision = tradeUseUSDCTerms
                ? USDC_TERMS_PRECISION
                : contractPriceStep.amount_precision;
              // Floor to the step and write it out at the STEP's precision.
              // `toFixed(precision)` rounded the floored value back up whenever
              // the token config's precision was coarser than the step: a BTC
              // balance of 0.00000148 (step 1e-8) became 0.0000015 and failed
              // "Insufficient buying power".
              setValue(
                PerpFormFieldKeyEnum.AMOUNT,
                floorToStepString(value, stepSize, precision),
                { shouldValidate: true }
              );
            },
          })}
          style={{ paddingBottom: 0 }}
          errors={errors}
          showValidationError={showAmountValidation}
          warning={
            insufficientLiquidity &&
            orderType === OrderTypeResponse.Market
              ? formTranslations("insufficient_market_liquidity")
              : balanceUnknownNote
          }
        />
        {/* Perp and spot alike: 25/50/75/MAX and the slider (decision 1). */}
        {(isPerp || isSpot) && (
          <>
            <QuickActions
              presets={[...SIZE_PRESETS]}
              active={activeSizePercent}
              onSelect={setSizePercent}
              max={sizeMax}
              disabled={isLoading}
            />
            <PercentSlider
              value={sizePercent}
              onChange={setSizePercent}
              disabled={isLoading || !sizeMax.ok}
              ariaLabel={formTranslations("size_percent_aria")}
            />
          </>
        )}
        {isMarketOrder && (
          <SlippageControl
            bps={slippageBps}
            onChange={setSlippageBps}
            disabled={isLoading}
            collapsible
            consequence={worstFill}
          />
        )}
      </GapWrapper>
    </InputsWrapper>
  );
}
